import React, { useCallback, useState, useRef, useEffect } from "react";
import ReactTooltip from "react-tooltip";
import { usePlaidLink } from "react-plaid-link";
import { useFormikContext } from "formik";
import toast from "react-hot-toast";
import SlidingToggle from "../../../../../../components/form/sliding-checkbox";
import InfoIcon from "../../../../../../assets/svg/icons/common/info";
import FancyCheckbox from "../../../../../../components/form/fancy-checkbox";
import TextInput from "../../../../../../components/form/text-input";
import HeadingWithLine from "../../../../../../components/HeadingWithLine/HeadingWithLine";
import BankChequeImg from "../../../../../../../../images/v2/bank_cheque.png";
import CaretDown from "../../../../../../assets/svg/icons/common/CaretDown";
import CrossRedCircleIcon from "../../../../../../assets/svg/icons/common/cross-red-circle";
import useSalesFormStore from "../../../../../../stores/sales-form";
import useDomainStore from "../../../../../../stores/domain";
import useUserStore from "../../../../../../stores/user";
import Switch from "../../../../../../components/form/switch";
import Spinner from "../../../../../../components/Loaders/Spinner";
import { plaidConnect, plaidSuccess } from "../../../../../../../api";
import { useEMSValidation } from "../../../../../../hooks/useEMSValidation";
import { CC_LOCKABLE_FIELDS } from "../../../../utils/validation";
import styles from "./style.module.scss";
import baseStyles from "../../style.module.scss";
import baseLayoutStyles from "../../base.module.scss";

// Tooltip data
const ToolTipBankAccountInfo =
  "Please include the full number all zeros before or after.";
const ToolTipRouting =
  'US routing numbers can be found online by searching (bank name) + "routing number" + (your state abbreviation) or refer to the bottom of a check.';
const ToolTipMaxSale =
  "If you're a new business with no prior processing put $5,000 unless you're sure that you will need more.";
const ToolTipAnnualRevenue =
  "This should include all deposits (cash, credit card, check, wire) into your business bank account on a monthly basis.";
const ToolTipTicketHighest =
  "The highest common ticket size you see from customers in one transaction. May be multiple items purchased at once.";
const ToolTipTicketAverage =
  "The average amount a customer will spend in one transaction.";

/**
 * Validates input to only allow numbers and decimals
 * @param {Event} e - The input event
 */
const handleNumberInput = (e) => {
  // Allow only numbers, decimal point, and navigation keys
  if (
    !/[\d.]/.test(e.key) &&
    e.key !== "Backspace" &&
    e.key !== "Delete" &&
    e.key !== "ArrowLeft" &&
    e.key !== "ArrowRight" &&
    e.key !== "Tab"
  ) {
    e.preventDefault();
  }

  // Prevent multiple decimal points
  if (e.key === "." && e.target.value.includes(".")) {
    e.preventDefault();
  }
};

/**
 * Validates input to only allow whole numbers (no decimals)
 * @param {Event} e - The input event
 */
const handleIntegerInput = (e) => {
  if (
    !/\d/.test(e.key) &&
    e.key !== "Backspace" &&
    e.key !== "Delete" &&
    e.key !== "ArrowLeft" &&
    e.key !== "ArrowRight" &&
    e.key !== "Tab"
  ) {
    e.preventDefault();
  }
};

/**
 * Filters a value to only keep numeric characters (no decimal point)
 * @param {string} value - The input value
 * @returns {string} Filtered value
 */
const filterIntegerValue = (value) => value.replace(/[^\d]/g, "");

/**
 * Filters a value to only keep numeric characters and one decimal point
 * @param {string} value - The input value
 * @returns {string} Filtered value
 */
const filterNumericValue = (value) => {
  // First remove any non-numeric characters except decimal point
  let filtered = value.replace(/[^\d.]/g, "");

  // Handle multiple decimal points by keeping only the first one
  const parts = filtered.split(".");
  if (parts.length > 2) {
    filtered = parts[0] + "." + parts.slice(1).join("");
  }

  return filtered;
};

/**
 * Headless launcher for Plaid Link.
 * Mounted only once a link_token is available; it opens the Plaid modal as
 * soon as the SDK is ready and reports completion via onSuccess / closure
 * via onExit.
 */
const PlaidLinkLauncher = ({ token, onSuccess, onExit }) => {
  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });

  useEffect(() => {
    if (ready) {
      open();
    }
  }, [ready, open]);

  return null;
};

/**
 * CreditCardProcessing Component
 * Renders a form for collecting credit card processing information including
 * acceptance methods, sales percentages by category, and bank account details
 */
const CreditCardProcessing = () => {
  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    setFieldError,
    setTouched,
    handleBlur,
  } = useFormikContext();
  const user = useUserStore((state) => state.user);
  const isLocked = !user && !!values.cc_processing_locked;
  const { isKurvDomain } = useDomainStore();
  const [bankAccountVerification, setBankAccountVerification] = useState(false);
  // Plaid Link state — set after /plaid-connect returns a link_token.
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  const [plaidId, setPlaidId] = useState(null);
  const applicationData = useSalesFormStore((state) => state.applicationData);
  // OTP token for the anonymous merchant flow. Empty for logged-in agents
  // (who pass the session-auth check on the server regardless).
  const otpCode = useSalesFormStore((state) => state.otpCode);
  const isPlaidConnected = useSalesFormStore(
    (state) => state.isPlaidConnected,
  );
  const plaidConnectLoading = useSalesFormStore(
    (state) => state.plaidConnectLoading,
  );
  const setPlaidConnectLoading = useSalesFormStore(
    (state) => state.setPlaidConnectLoading,
  );
  const setIsPlaidConnected = useSalesFormStore(
    (state) => state.setIsPlaidConnected,
  );
  const setHasPlaidRoutingAndAccountNumber = useSalesFormStore(
    (state) => state.setHasPlaidRoutingAndAccountNumber,
  );
  const hasPlaidRoutingAndAccountNumber = useSalesFormStore(
    (state) => state.hasPlaidRoutingAndAccountNumber,
  );
  const {
    validateRoutingNumber,
    validatingRoutingNumber,
    validationStatuses,
    setValidationStatus,
  } = useEMSValidation();

  const mountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Step 1: request a Plaid link_token from EMS. Storing it mounts the
  // PlaidLinkLauncher, which opens the Plaid Link modal.
  const handlePlaidConnect = useCallback(
    async (applicationData) => {
      setPlaidConnectLoading(true);
      try {
        const response = await plaidConnect(applicationData, {
          headers: { OtpCode: otpCode },
        });
        if (response?.status && response?.data?.link_token) {
          setPlaidId(response.data.plaid_id);
          setPlaidLinkToken(response.data.link_token);
        } else {
          toast.error("Failed to initialize bank verification. Please try again.");
          setPlaidConnectLoading(false);
        }
      } catch (error) {
        toast.error("Failed to initialize bank verification. Please try again.");
        setPlaidConnectLoading(false);
      }
    },
    [setPlaidConnectLoading, otpCode],
  );

  // Step 2: Plaid Link succeeded — exchange the public_token via EMS and
  // populate the bank fields with the returned account details.
  const handlePlaidLinkSuccess = useCallback(
    (public_token) => {
      setPlaidLinkToken(null);
      plaidSuccess(
        {
          plaid_id: plaidId,
          public_token,
          editslug: applicationData?.editslug,
        },
        { headers: { OtpCode: otpCode } },
      )
        .then((response) => {
          // Only treat the merchant as "connected" when routing AND account
          // actually came back — otherwise the manual bank fields get disabled
          // with no values, leaving the merchant stuck and unable to retry.
          if (response?.Success && response?.RoutingNum && response?.AccountNum) {
            setFieldValue("bank_routing", response?.RoutingNum);
            setFieldValue("bank_name", response?.BankName);
            setFieldValue("bank_account", response?.AccountNum);
            setIsPlaidConnected(true);
            setFieldError("bank_routing", null);
            setFieldError("bank_account", null);
            setValidationStatus("bank_routing", undefined);
            setHasPlaidRoutingAndAccountNumber(true);
          } else {
            toast.error("Bank verification failed. Please try again.");
          }
        })
        .catch(() => {
          toast.error("Failed to save bank verification. Please try again.");
        })
        .finally(() => {
          setPlaidConnectLoading(false);
        });
    },
    [
      plaidId,
      applicationData,
      setFieldValue,
      setIsPlaidConnected,
      setHasPlaidRoutingAndAccountNumber,
      setPlaidConnectLoading,
      otpCode,
    ],
  );

  // Plaid Link closed without completing (user exit or error).
  const handlePlaidLinkExit = useCallback(
    (error) => {
      setPlaidLinkToken(null);
      setPlaidConnectLoading(false);
      if (error) {
        toast.error("Bank verification was not completed.");
      }
    },
    [setPlaidConnectLoading],
  );

  const debouncedValidateRoutingNumber = useCallback(
    (routingNumber) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        if (!mountedRef.current) return;
        if (validatingRoutingNumber) return;

        const response = await validateRoutingNumber(
          routingNumber,
          setFieldError,
        );

        if (response?.status && response?.bankName) {
          setFieldValue("bank_name", response.bankName);
        }
      }, 500);
    },
    [
      validatingRoutingNumber,
      validateRoutingNumber,
      setFieldError,
      setFieldValue,
    ],
  );

  const handleRoutingNumberChange = async (event) => {
    const routingNumber = event.target.value;
    handleChange(event);

    if (
      !mountedRef.current ||
      isPlaidConnected ||
      hasPlaidRoutingAndAccountNumber
    )
      return;

    debouncedValidateRoutingNumber(routingNumber);
  };

  const handleRoutingNumberBlur = async (event) => {
    const routingNumber = event.target.value;
    handleBlur(event);

    if (
      !mountedRef.current ||
      isPlaidConnected ||
      hasPlaidRoutingAndAccountNumber
    )
      return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // Validate immediately on blur
    const digitsOnly = routingNumber.replace(/\D/g, "");
    if (digitsOnly.length === 9 && !validatingRoutingNumber) {
      const response = await validateRoutingNumber(
        routingNumber,
        setFieldError,
      );
      if (response?.status && response?.bankName) {
        setFieldValue("bank_name", response.bankName);
      }
    }
  };

  return (
    <div
      className={`${baseStyles.sectionContainer} ${isKurvDomain ? styles.kurv_theme : ""}`}
    >
      <ReactTooltip className={styles.tooltip} />

      <div className={baseStyles.sectionHeader}>
        <h6 className={baseStyles.sectionTitle}>Credit Card Processing</h6>
      </div>

      <div className={baseStyles.sectionFormContent}>
        <div className={`${styles.formGroup} ${isLocked ? styles.locked : ""}`}>
          <div className={styles.labelRow}>
            <label
              htmlFor="currently_accept_vdmax"
              className={styles.label}
              id="accept_cards_label"
            >
              Do you currently accept Visa, MasterCard, Discover and/or AMEX?
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            </label>
            {user && (
              <Switch
                id="cc_processing_locked"
                name="cc_processing_locked"
                label={values.cc_processing_locked ? "Locked" : "Unlocked"}
                labelPosition="left"
                checked={!!values.cc_processing_locked}
                onChange={({ target }) => {
                  setFieldValue("cc_processing_locked", target.checked);
                  if (target.checked) {
                    setTouched(
                      {
                        ...touched,
                        ...Object.fromEntries(
                          CC_LOCKABLE_FIELDS.map((field) => [field, true]),
                        ),
                      },
                      false,
                    );
                  }
                }}
              />
            )}
          </div>
          <SlidingToggle
            options={["yes", "no"]}
            width="116px"
            value={values.currently_accept_vdmax}
            onChange={(value) => setFieldValue("currently_accept_vdmax", value)}
            aria-labelledby="accept_cards_label"
            aria-describedby="accept_cards_error"
            disabled={isLocked}
          />
          {touched.currently_accept_vdmax && errors.currently_accept_vdmax && (
            <div className={styles.error} role="alert">
              {errors.currently_accept_vdmax}
            </div>
          )}
          {values.currently_accept_vdmax === "yes" && (
            <div className={styles.alert} role="note">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8C14.4982 6.27665 13.8128 4.62441 12.5942 3.40582C11.3756 2.18722 9.72335 1.50182 8 1.5ZM7.75 4.5C7.89834 4.5 8.04334 4.54399 8.16668 4.6264C8.29002 4.70881 8.38615 4.82594 8.44291 4.96299C8.49968 5.10003 8.51453 5.25083 8.48559 5.39632C8.45665 5.5418 8.38522 5.67544 8.28033 5.78033C8.17544 5.88522 8.04181 5.95665 7.89632 5.98559C7.75083 6.01453 7.60003 5.99968 7.46299 5.94291C7.32595 5.88614 7.20881 5.79001 7.1264 5.66668C7.04399 5.54334 7 5.39834 7 5.25C7 5.05109 7.07902 4.86032 7.21967 4.71967C7.36032 4.57902 7.55109 4.5 7.75 4.5ZM8.5 11.5C8.23479 11.5 7.98043 11.3946 7.7929 11.2071C7.60536 11.0196 7.5 10.7652 7.5 10.5V8C7.36739 8 7.24022 7.94732 7.14645 7.85355C7.05268 7.75979 7 7.63261 7 7.5C7 7.36739 7.05268 7.24021 7.14645 7.14645C7.24022 7.05268 7.36739 7 7.5 7C7.76522 7 8.01957 7.10536 8.20711 7.29289C8.39465 7.48043 8.5 7.73478 8.5 8V10.5C8.63261 10.5 8.75979 10.5527 8.85356 10.6464C8.94732 10.7402 9 10.8674 9 11C9 11.1326 8.94732 11.2598 8.85356 11.3536C8.75979 11.4473 8.63261 11.5 8.5 11.5Z"
                  fill="#626168"
                />
              </svg>
              <p>
                Be ready to provide the name of the provider along with your 3
                most previous months of processing statements
              </p>
            </div>
          )}
        </div>

        <div className={`${styles.formGroup} ${isLocked ? styles.locked : ""}`}>
          <label className={styles.label} id="card_acceptance_methods_label">
            Select all ways you accept credit & debit cards
          </label>

          <div className={styles.checkboxGroup} role="group">
            <FancyCheckbox
              label="Online"
              id="website"
              name="ways_accepting_cards"
              value={"Website"}
              className={styles.checkboxItem}
              disabled={isLocked}
              checked={values.ways_accepting_cards.includes("Website")}
              onChange={(event) => {
                if (event.target.checked) {
                  setFieldValue("ways_accepting_cards", [
                    ...values.ways_accepting_cards,
                    "Website",
                  ]);
                } else {
                  setFieldValue(
                    "ways_accepting_cards",
                    values.ways_accepting_cards.filter(
                      (card) => card !== "Website",
                    ),
                  );
                }
              }}
              touched={touched.ways_accepting_cards}
              error={errors.ways_accepting_cards}
            />

            <FancyCheckbox
              label="In-Store"
              id="in_store"
              name="ways_accepting_cards"
              className={styles.checkboxItem}
              disabled={isLocked}
              value={"In-Store"}
              checked={values.ways_accepting_cards.includes("In-Store")}
              onChange={(event) => {
                if (event.target.checked) {
                  setFieldValue("ways_accepting_cards", [
                    ...values.ways_accepting_cards,
                    "In-Store",
                  ]);
                } else {
                  setFieldValue(
                    "ways_accepting_cards",
                    values.ways_accepting_cards.filter(
                      (card) => card !== "In-Store",
                    ),
                  );
                }
              }}
              touched={touched.ways_accepting_cards}
              error={errors.ways_accepting_cards}
            />

            <FancyCheckbox
              label="On the Go"
              id="on_the_go"
              name="ways_accepting_cards"
              value={"On the Go"}
              className={styles.checkboxItem}
              disabled={isLocked}
              checked={values.ways_accepting_cards.includes("On the Go")}
              onChange={(event) => {
                if (event.target.checked) {
                  setFieldValue("ways_accepting_cards", [
                    ...values.ways_accepting_cards,
                    "On the Go",
                  ]);
                } else {
                  setFieldValue(
                    "ways_accepting_cards",
                    values.ways_accepting_cards.filter(
                      (card) => card !== "On the Go",
                    ),
                  );
                }
              }}
              touched={touched.ways_accepting_cards}
              error={errors.ways_accepting_cards}
            />

            <FancyCheckbox
              label="Over the phone"
              id="over_the_phone"
              name="ways_accepting_cards"
              value={"Over the Phone"}
              className={styles.checkboxItem}
              disabled={isLocked}
              checked={values.ways_accepting_cards.includes("Over the Phone")}
              onChange={(event) => {
                if (event.target.checked) {
                  setFieldValue("ways_accepting_cards", [
                    ...values.ways_accepting_cards,
                    "Over the Phone",
                  ]);
                } else {
                  setFieldValue(
                    "ways_accepting_cards",
                    values.ways_accepting_cards.filter(
                      (card) => card !== "Over the Phone",
                    ),
                  );
                }
              }}
              touched={touched.ways_accepting_cards}
              error={errors.ways_accepting_cards}
            />
          </div>
          {touched.ways_accepting_cards && errors.ways_accepting_cards && (
            <span style={{ marginTop: "-4px" }} className={styles.error}>
              {errors.ways_accepting_cards}
            </span>
          )}
        </div>

        <div className={`${styles.card} ${isLocked ? styles.locked : ""}`}>
          <label htmlFor="" className={styles.label}>
            Enter percentage of your sales for each category below
            <span className={styles.required}>*</span>
          </label>
          <div className={styles.formInputGrid}>
            <TextInput
              label="Sales Swiped"
              placeholder="0"
              inputMode="decimal"
              id="sales_swiped"
              name="sales_swiped"
              value={values.sales_swiped}
              onChange={(e) => {
                const filteredValue = filterIntegerValue(e.target.value);
                setFieldValue("sales_swiped", filteredValue);
              }}
              onKeyPress={handleIntegerInput}
              suffix="%"
              disabled={isLocked}
              touched={touched.sales_swiped}
              errored={!!errors.sales_swiped}
              error={errors.sales_swiped}
            />

            <TextInput
              label="Sales Keyed-In"
              placeholder="0"
              inputMode="decimal"
              id="sales_keyed"
              name="sales_keyed"
              value={values.sales_keyed}
              onChange={(e) => {
                const filteredValue = filterIntegerValue(e.target.value);
                setFieldValue("sales_keyed", filteredValue);
              }}
              onKeyPress={handleIntegerInput}
              suffix="%"
              disabled={isLocked}
              touched={touched.sales_keyed}
              errored={!!errors.sales_keyed}
              error={errors.sales_keyed}
            />

            <TextInput
              label="Online sales"
              inputMode="decimal"
              placeholder="0"
              id="internet"
              name="internet"
              value={values.internet}
              onChange={(e) => {
                const filteredValue = filterIntegerValue(e.target.value);
                setFieldValue("internet", filteredValue);
              }}
              onKeyPress={handleIntegerInput}
              suffix="%"
              disabled={isLocked}
              touched={touched.internet}
              errored={!!errors.internet}
              error={errors.internet}
              aria-describedby="sales_total_display"
            />
          </div>

          <div className={styles.total}>
            <p className={styles.totalLabel}>Total</p>
            <p className={styles.totalValue}>
              {(() => {
                const total =
                  Number(values.sales_swiped || 0) +
                  Number(values.sales_keyed || 0) +
                  Number(values.internet || 0);
                return `${total.toFixed(2)}%`;
              })()}
            </p>
            {(() => {
              const total =
                Number(values.sales_swiped || 0) +
                Number(values.sales_keyed || 0) +
                Number(values.internet || 0);
              return total === 100 ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96451 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7473 9.41498 20.7192 6.93661 18.8913 5.10872C17.0634 3.28084 14.585 2.25273 12 2.25ZM16.2806 10.2806L11.0306 15.5306C10.961 15.6004 10.8783 15.6557 10.7872 15.6934C10.6962 15.7312 10.5986 15.7506 10.5 15.7506C10.4014 15.7506 10.3038 15.7312 10.2128 15.6934C10.1218 15.6557 10.039 15.6004 9.96938 15.5306L7.71938 13.2806C7.57865 13.1399 7.49959 12.949 7.49959 12.75C7.49959 12.551 7.57865 12.3601 7.71938 12.2194C7.86011 12.0786 8.05098 11.9996 8.25 11.9996C8.44903 11.9996 8.6399 12.0786 8.78063 12.2194L10.5 13.9397L15.2194 9.21937C15.2891 9.14969 15.3718 9.09442 15.4628 9.0567C15.5539 9.01899 15.6515 8.99958 15.75 8.99958C15.8486 8.99958 15.9461 9.01899 16.0372 9.0567C16.1282 9.09442 16.2109 9.14969 16.2806 9.21937C16.3503 9.28906 16.4056 9.37178 16.4433 9.46283C16.481 9.55387 16.5004 9.65145 16.5004 9.75C16.5004 9.84855 16.481 9.94613 16.4433 10.0372C16.4056 10.1282 16.3503 10.2109 16.2806 10.2806Z"
                    fill="#37B450"
                  />
                </svg>
              ) : (
                <CrossRedCircleIcon size={20} />
              );
            })()}
          </div>
        </div>

        <div className={`${styles.formGroup} ${isLocked ? styles.locked : ""}`}>
          <label
            htmlFor="recurring_bill_triel_radio"
            className={styles.label}
            id="recurring_billing_label"
          >
            Recurring Billing
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          </label>
          <SlidingToggle
            options={["yes", "no"]}
            width="116px"
            value={values.recurring_bill_triel_radio}
            onChange={(value) =>
              setFieldValue("recurring_bill_triel_radio", value)
            }
            aria-labelledby="recurring_billing_label"
            aria-describedby="recurring_billing_error"
            disabled={isLocked}
          />
          {touched.recurring_bill_triel_radio &&
            errors.recurring_bill_triel_radio && (
              <div
                id="recurring_billing_error"
                className={styles.error}
                role="alert"
              >
                {errors.recurring_bill_triel_radio}
              </div>
            )}
        </div>

        <div
          className={`${baseLayoutStyles.flex} ${baseLayoutStyles.gap16px} ${baseLayoutStyles.flexCol}`}
        >
          <HeadingWithLine text="Processing Amounts" margin={false} />

          <div className={`${styles.formInputGrid} ${isLocked ? styles.locked : ""}`}>
            <TextInput
              label="Max Monthly Sales"
              placeholder="0.00"
              id="monthly_max_sales"
              name="monthly_max_sales"
              value={values.monthly_max_sales}
              onChange={(e) => {
                const filteredValue = filterNumericValue(e.target.value);
                setFieldValue("monthly_max_sales", filteredValue);
              }}
              onKeyPress={handleNumberInput}
              prefix="$"
              disabled={isLocked}
              suffix={<InfoIcon data-tip={ToolTipMaxSale} />}
              touched={touched.monthly_max_sales}
              errored={!!errors.monthly_max_sales}
              error={errors.monthly_max_sales}
            />

            <TextInput
              label="Average Sale Amount"
              placeholder="0.00"
              id="avg_sale_amount"
              name="avg_sale_amount"
              value={values.avg_sale_amount}
              onChange={(e) => {
                const filteredValue = filterNumericValue(e.target.value);
                setFieldValue("avg_sale_amount", filteredValue);
              }}
              onKeyPress={handleNumberInput}
              prefix="$"
              disabled={isLocked}
              suffix={<InfoIcon data-tip={ToolTipTicketAverage} />}
              touched={touched.avg_sale_amount}
              errored={!!errors.avg_sale_amount}
              error={errors.avg_sale_amount}
            />

            <TextInput
              label="Highest Sale Amount"
              placeholder="0.00"
              id="high_sale_amount"
              name="high_sale_amount"
              value={values.high_sale_amount}
              onChange={(e) => {
                const filteredValue = filterNumericValue(e.target.value);
                setFieldValue("high_sale_amount", filteredValue);
              }}
              onKeyPress={handleNumberInput}
              prefix="$"
              disabled={isLocked}
              suffix={<InfoIcon data-tip={ToolTipTicketHighest} />}
              touched={touched.high_sale_amount}
              errored={!!errors.high_sale_amount}
              error={errors.high_sale_amount}
            />
          </div>
        </div>

        <div
          className={`${baseLayoutStyles.flex} ${baseLayoutStyles.gap16px} ${baseLayoutStyles.flexCol}`}
        >
          <HeadingWithLine text="Business Bank Details" margin={false} />

          <div className={styles.fastTrackButtonContainer}>
            <button
              className={`${styles.fastTrackButton} ${isPlaidConnected ? styles.connected : ""} ${plaidConnectLoading ? styles.loading : ""}`}
              onClick={
                isPlaidConnected
                  ? undefined
                  : () => handlePlaidConnect(applicationData)
              }
              type="button"
            >
              <span className={styles.fastTrackButtonText}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                >
                  <path
                    d="M20.5491 11.7617L10.0491 23.0117C9.93784 23.1305 9.79097 23.2098 9.63065 23.2377C9.47034 23.2657 9.30528 23.2407 9.16039 23.1667C9.0155 23.0926 8.89863 22.9734 8.82743 22.827C8.75623 22.6807 8.73455 22.5152 8.76568 22.3555L10.14 15.4808L4.73724 13.452C4.62121 13.4086 4.51773 13.3372 4.43606 13.244C4.35439 13.1508 4.29706 13.0389 4.26921 12.9182C4.24135 12.7975 4.24383 12.6718 4.27642 12.5522C4.30902 12.4327 4.37071 12.3231 4.45599 12.2333L14.956 0.983274C15.0673 0.864526 15.2141 0.785191 15.3744 0.75724C15.5348 0.729289 15.6998 0.75424 15.8447 0.828326C15.9896 0.902412 16.1065 1.02161 16.1777 1.16794C16.2489 1.31428 16.2705 1.47979 16.2394 1.63952L14.8613 8.52171L20.2641 10.5476C20.3793 10.5913 20.4819 10.6627 20.563 10.7555C20.644 10.8483 20.7009 10.9596 20.7287 11.0796C20.7565 11.1996 20.7544 11.3246 20.7224 11.4436C20.6904 11.5626 20.6296 11.6718 20.5454 11.7617H20.5491Z"
                    fill="white"
                  />
                </svg>
                {!isPlaidConnected
                  ? "Connect to Plaid"
                  : "Plaid verified"}
              </span>

              {plaidConnectLoading && (
                <Spinner size={24} color="#FFFFFF" secondaryColor="#FFFFFF" />
              )}
            </button>

            {plaidLinkToken && (
              <PlaidLinkLauncher
                token={plaidLinkToken}
                onSuccess={handlePlaidLinkSuccess}
                onExit={handlePlaidLinkExit}
              />
            )}
          </div>
          <h5 className={styles.orText}>OR</h5>

          <div className={styles.card}>
            <div className={styles.enterOwnBankDetails}>
              Enter your bank account details
            </div>
            <div className={styles.formInputGrid}>
              <TextInput
                label="Routing Number"
                placeholder="Routing Number"
                id="bank_routing"
                name="bank_routing"
                value={values.bank_routing}
                disabled={
                  isPlaidConnected || hasPlaidRoutingAndAccountNumber
                }
                onChange={handleRoutingNumberChange}
                onBlur={handleRoutingNumberBlur}
                suffix={
                  validatingRoutingNumber ? (
                    <Spinner size={16} />
                  ) : (
                    <InfoIcon data-tip={ToolTipRouting} />
                  )
                }
                touched={touched.bank_routing}
                errored={
                  errors.bank_routing ||
                  validationStatuses?.bank_routing === false
                }
                error={
                  errors.bank_routing ||
                  (validationStatuses?.bank_routing === false
                    ? "* Enter a valid routing number"
                    : null)
                }
                cleaveOptions={{
                  blocks: [9],
                  numericOnly: true,
                  maxLength: 9,
                  onValueChanged: handleRoutingNumberChange,
                }}
              />

              <TextInput
                label="Account Number"
                placeholder="Account Number"
                id="bank_account"
                name="bank_account"
                type="number"
                value={values.bank_account}
                disabled={
                  isPlaidConnected || hasPlaidRoutingAndAccountNumber
                }
                onChange={(e) => {
                  setFieldValue("bank_account", e.target.value);
                }}
                onBlur={handleBlur}
                suffix={<InfoIcon data-tip={ToolTipBankAccountInfo} />}
                touched={touched.bank_account}
                errored={!!errors.bank_account}
                error={errors.bank_account}
              />

              <TextInput
                label="Bank Name"
                placeholder="Bank Name"
                id="bank_name"
                name="bank_name"
                value={values.bank_name}
                onChange={(e) => {
                  setFieldValue("bank_name", e.target.value);
                }}
                onBlur={handleBlur}
                touched={touched.bank_name}
                errored={!!errors.bank_name}
                error={errors.bank_name}
              />
            </div>
            <div className={styles.imageContainer}>
              <img src={BankChequeImg} alt="credit-card-processing" />
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div
            className={`${styles.cardHeader} ${bankAccountVerification ? styles.open : ""}`}
            onClick={() => setBankAccountVerification(!bankAccountVerification)}
          >
            <CaretDown />
            <p>Bank Account Verification</p>
          </div>

          {bankAccountVerification && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Select all services that you're interested in
              </label>
              <div className={`${styles.checkboxGroup}`}>
                <FancyCheckbox
                  label="Fraud & Chargeback tools"
                  id="fraud_chargeback_tool"
                  name="fraud_chargeback_tool"
                  className={styles.checkboxItem}
                  value={values.fraud_chargeback_tool}
                  onChange={(e) => {
                    setFieldValue(
                      "fraud_chargeback_tool",
                      e.target.checked ? "yes" : "no",
                    );
                  }}
                  checked={values.fraud_chargeback_tool === "yes"}
                />

                <FancyCheckbox
                  label="Accepting eCheck"
                  id="cheque_processing"
                  name="cheque_processing"
                  className={styles.checkboxItem}
                  value={"cheque_processing"}
                  onChange={(e) => {
                    setFieldValue(
                      "cheque_processing",
                      e.target.checked ? "yes" : "no",
                    );
                  }}
                  checked={values.cheque_processing === "yes"}
                />

                <FancyCheckbox
                  label="Business Funding"
                  id="business_funding_required"
                  name="business_funding_required"
                  className={styles.checkboxItem}
                  value={"business_funding_required"}
                  onChange={(e) => {
                    setFieldValue(
                      "business_funding_required",
                      e.target.checked,
                    );
                  }}
                  checked={values.business_funding_required}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditCardProcessing;
